import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, defer, of, switchMap, throwError, timer } from "rxjs";
import { catchError, concatMap, dematerialize, map, materialize, retry, shareReplay, take, takeWhile } from "rxjs/operators";
import { environment } from "src/environments/environment";

export interface SecureStorageMerchantSummary {
    id: number;
    name: string;
    legalName?: string | null;
    merchantCode?: string | null;
    industry?: string | null;
    website?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    region?: string | null;
    postalCode?: string | null;
    countryCode?: string | null;
    primaryContactName: string | null;
    primaryContactTitle?: string | null;
    primaryContactEmail?: string | null;
    primaryContactPhone?: string | null;
    supportContactEmail?: string | null;
    supportContactPhone?: string | null;
    accessibleAssessmentCount: number;
}

export interface SecureStorageDashboardMerchantSummary extends SecureStorageMerchantSummary {
    supportContactName: string | null;
    activeAssessmentCount: number;
    expiredAssessmentCount: number;
    secureStorageFileCount: number;
    totalFileSizeBytes: number;
    aiInputTokens: number;
    aiOutputTokens: number;
}

export interface SecureStorageExpectedScan {
    jobId: number;
    sourceHash: string;
    sourceVersion: string;
}

export interface SecureStorageScanPolicy {
    failureCategory: string;
    failureReason: string | null;
    retryAllowed: boolean;
    automaticRetryAllowed: boolean;
    overrideRequired: boolean;
    expectedScan: SecureStorageExpectedScan | null;
    overrideApplied: boolean;
    overrideConfirmedByUserId: number | null;
    overrideConfirmedAt: string | null;
    overrideConfirmedByDisplayName: string | null;
}

export interface SecureStorageScanEnqueueOptions {
    overrideAcknowledged?: boolean;
    expectedScan?: SecureStorageExpectedScan;
}

export function normalizeSecureStorageScanPolicy(value: unknown): SecureStorageScanPolicy | null {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const record = value as Record<string, unknown>;
    if (!["document_injection", "model_refusal", "moderation", "unsupported_extraction", "malware",
        "safety_unavailable", "transient", "failure", "reanalysis"].includes(String(record["failureCategory"])) ||
        ["retryAllowed", "automaticRetryAllowed", "overrideRequired", "overrideApplied"].some(key => typeof record[key] !== "boolean")) return null;
    const raw = record["expectedScan"];
    let expectedScan: SecureStorageExpectedScan | null = null;
    if (raw !== null && raw !== undefined) {
        if (typeof raw !== "object" || Array.isArray(raw)) return null;
        const target = raw as Record<string, unknown>;
        if (typeof target["jobId"] !== "number" || !Number.isSafeInteger(target["jobId"]) || target["jobId"] <= 0 ||
            typeof target["sourceHash"] !== "string" || !target["sourceHash"] || target["sourceHash"].length > 128 ||
            typeof target["sourceVersion"] !== "string" || !/^[a-f0-9]{64}$/.test(target["sourceVersion"])) return null;
        expectedScan = { jobId: target["jobId"], sourceHash: target["sourceHash"], sourceVersion: target["sourceVersion"] };
    }
    if (record["overrideRequired"] && (!record["retryAllowed"] || !expectedScan)) return null;
    if (record["failureCategory"] === "model_refusal" &&
        (record["retryAllowed"] || record["automaticRetryAllowed"] || record["overrideRequired"])) return null;
    const applied = record["overrideApplied"] === true;
    const userId = record["overrideConfirmedByUserId"];
    const confirmedAt = record["overrideConfirmedAt"];
    const name = record["overrideConfirmedByDisplayName"];
    if (applied && (typeof userId !== "number" || !Number.isSafeInteger(userId) || userId <= 0 ||
        typeof confirmedAt !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(confirmedAt) ||
        typeof name !== "string" || !name.trim() || name.length > 255)) return null;
    return {
        failureCategory: record["failureCategory"] as string,
        failureReason: typeof record["failureReason"] === "string" ? record["failureReason"].slice(0, 500) : null,
        retryAllowed: record["retryAllowed"] === true,
        automaticRetryAllowed: record["automaticRetryAllowed"] === true,
        overrideRequired: record["overrideRequired"] === true,
        expectedScan, overrideApplied: applied,
        overrideConfirmedByUserId: applied ? userId as number : null,
        overrideConfirmedAt: applied ? confirmedAt as string : null,
        overrideConfirmedByDisplayName: applied ? name as string : null
    };
}

export interface SecureStorageFileSummary {
    merchantId?: number;
    merchantName?: string;
    id: number;
    fileName: string;
    mimeType?: string | null;
    createdAt: string | null;
    uploadedAt: string | null;
    lastUpdatedAt: string | null;
    uploadedByDisplayName: string | null;
    fileSizeBytes: number | null;
    aiScanStatus?: string | null;
    aiScanPolicy?: SecureStorageScanPolicy | null;
    aiScanDetails?: unknown;
    aiScanErrorCode?: string | null;
    aiScanErrorMessage?: string | null;
    aiLastReadTimestamp?: string | null;
    aiFileSummary?: string | null;
    piiDetected?: string[] | null;
    malwareScanStatus?: string | null;
    malwareScanDetails?: unknown;
    tags: Record<string, string>;
    aiFileTags?: Record<string, string>;
    aiFileAnnotations?: unknown;
    annotations?: unknown;
    previewSupported?: boolean;
    previewStatus?: SecureStoragePreviewStatus | null;
}

export interface SecureStorageFileAccessResult {
    id: number;
    fileName: string;
    mimeType: string | null;
    fileSizeBytes: number | null;
    accessUrl: string;
}

export interface SecureStorageUploadPreviewItem {
    [key: string]: unknown;
    id: string;
    fileName: string;
    fileSizeBytes: number | null;
    mimeType: string | null;
    replacedExisting?: boolean;
    archiveSourceName?: string | null;
    archiveEntryName?: string | null;
}

export interface SecureStorageMerchantContextResult {
    merchant: SecureStorageMerchantSummary | null;
    errorMessage: string | null;
}

export interface SecureStorageManageFilesPageData {
    merchant: SecureStorageMerchantSummary | null;
    files: SecureStorageFileSummary[];
    errorMessage: string | null;
}

export interface SecureStorageUploadResult {
    persisted: boolean;
    items: SecureStorageUploadPreviewItem[];
    message: string | null;
}

export interface SecureStorageUploadSubmission {
    submissionKey: string;
    expiresAt: string;
}

export interface SecureStorageUploadCompletion {
    completed: boolean;
    alreadyCompleted: boolean;
    successfulFileCount: number;
}

export interface SecureStorageEnqueueAiScanResult {
    queued: boolean;
}

export interface SecureStorageCancelAiScanResult {
    canceled: boolean;
    status: string;
}

export type SecureStoragePreviewStatusValue =
    | "not_requested"
    | "pending"
    | "running"
    | "succeeded"
    | "failed"
    | "unavailable"
    | "unsupported"
    | "missing_source_hash"
    | "malware_not_clean";

export interface SecureStoragePreviewStatus {
    fileId: number | null;
    fileName: string | null;
    mimeType: string | null;
    fileSizeBytes: number | null;
    previewSupported: boolean;
    previewable: boolean;
    status: SecureStoragePreviewStatusValue;
    message: string;
    previewAvailable: boolean;
    contentUrl: string | null;
    rendererVersion: string | null;
    requestedAt: string | null;
    lastRequestedAt: string | null;
    startedAt: string | null;
    completedAt: string | null;
    nextAttemptAt: string | null;
    previewByteCount: number | null;
    previewPageCount: number | null;
    previewTruncated: boolean;
    previewMetadata: Record<string, unknown> | null;
    errorCode: string | null;
    errorMessage: string | null;
}

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

interface SecureStorageMerchantFilesResponse {
    merchantId: number;
    items: SecureStorageFileSummary[];
}

interface SecureStorageFileAccessResponse {
    id: number;
    fileName: string;
    mimeType: string | null;
    fileSizeBytes: number | null;
    accessUrl: string;
}

type SecureStoragePreviewStatusResponse = SecureStoragePreviewStatus;

interface SecureStorageMerchantsResponse {
    items: SecureStorageDashboardMerchantSummary[];
}

interface SecureStorageUploadResponse {
    merchantId: number;
    persisted: boolean;
    items: SecureStorageUploadPreviewItem[];
}

interface SecureStorageDeleteFileResponse {
    id: number;
    archived: boolean;
}

interface SecureStorageUpdateTagsResponse {
    id: number;
    tags: Record<string, string>;
}

interface SecureStorageUpdateAnnotationsResponse {
    id: number;
    annotations: unknown;
    aiScanStatus?: string | null;
    aiScanPolicy?: unknown;
    aiScanDetails?: unknown;
    aiScanErrorCode?: string | null;
    aiScanErrorMessage?: string | null;
    lastUpdatedAt: string | null;
}

@Injectable({ providedIn: "root" })
export class SecureStorageService {
    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");
    private static readonly MISSING_MERCHANT_CONTEXT_MESSAGE =
        "A merchant selection is required before Secure Storage content can be loaded.";
    // Assignment changes can remove merchant access between link creation and page load.
    private static readonly ACCESS_DENIED_MERCHANT_CONTEXT_MESSAGE =
        "You do not have access to Secure Storage for the selected merchant. Your assigned merchants may have changed, so open a new encoded merchant link from the Secure Storage dashboard or assessment.";
    private static readonly DISABLED_ORGANIZATION_MESSAGE =
        "Secure Storage is disabled for your organization. Ask an Admin or Global Admin to enable it in account settings.";
    private static readonly LOAD_MERCHANT_CONTEXT_FAILURE_MESSAGE =
        "Secure Storage could not load the selected merchant.";
    private static readonly PREVIEW_UNAVAILABLE_MESSAGE = "File preview unavailable";

    constructor(private readonly http: HttpClient) {}

    listDashboardMerchants(): Observable<SecureStorageDashboardMerchantSummary[]> {
        return this.http
            .get<ApiResponse<SecureStorageMerchantsResponse>>(`${this.apiBaseUrl}/secure-storage/merchants`)
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to load Secure Storage merchants.");
                    }

                    const items = Array.isArray(response.data?.items) ? response.data.items : [];
                    return items.map((item) => ({
                        ...item,
                        aiInputTokens: Number((item as { aiInputTokens?: unknown; ai_input_tokens?: unknown }).aiInputTokens ?? (item as { ai_input_tokens?: unknown }).ai_input_tokens ?? 0) || 0,
                        aiOutputTokens: Number((item as { aiOutputTokens?: unknown; ai_output_tokens?: unknown }).aiOutputTokens ?? (item as { ai_output_tokens?: unknown }).ai_output_tokens ?? 0) || 0
                    }));
                })
            );
    }

    getMerchantContext(merchantId: number | null | undefined): Observable<SecureStorageMerchantContextResult> {
        if (!Number.isInteger(merchantId) || merchantId <= 0) {
            return of({
                merchant: null,
                errorMessage: SecureStorageService.MISSING_MERCHANT_CONTEXT_MESSAGE
            });
        }

        return this.http
            .get<ApiResponse<SecureStorageMerchantSummary>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/context`
            )
            .pipe(
                map((response) => ({
                    merchant: response?.success ? response.data ?? null : null,
                    errorMessage: response?.success
                        ? null
                        : this.resolveMerchantContextErrorMessage({
                              status: null,
                              message: response?.message
                          })
                })),
                catchError((error) =>
                    of({
                        merchant: null,
                        errorMessage: this.resolveMerchantContextErrorMessage({
                            status: error?.status,
                            message: error?.error?.message || error?.message
                        })
                    })
                )
            );
    }

    listOrganizationFiles(accountId: number): Observable<SecureStorageFileSummary[]> {
        if (!Number.isSafeInteger(accountId) || accountId < 1) throw new Error("Invalid organization.");
        return this.http.get<ApiResponse<{ accountId: number; items: SecureStorageFileSummary[] }>>(
            `${this.apiBaseUrl}/switch_accounts/${accountId}/secure-storage-files`).pipe(map(response => {
                if (!response.success || response.data?.accountId !== accountId || !Array.isArray(response.data.items))
                    throw new Error(response.message || "Unable to load organization files.");
                return response.data.items.map(file => ({ ...file, aiScanPolicy: normalizeSecureStorageScanPolicy(file.aiScanPolicy) }));
            }));
    }

    getManageFilesPageData(merchantId: number | null | undefined): Observable<SecureStorageManageFilesPageData> {
        return this.getMerchantContext(merchantId).pipe(
            switchMap((context) => {
                if (!context.merchant || context.errorMessage) {
                    return of({
                        merchant: context.merchant,
                        files: [],
                        errorMessage: context.errorMessage
                    });
                }

                return this.listFilesForMerchant(context.merchant.id).pipe(
                    map((files) => ({
                        merchant: context.merchant,
                        files,
                        errorMessage: null
                    })),
                    catchError(() =>
                        of({
                            merchant: context.merchant,
                            files: [],
                            errorMessage: "Secure Storage could not load files for the selected merchant."
                        })
                    )
                );
            })
        );
    }

    listFilesForMerchant(merchantId: number): Observable<SecureStorageFileSummary[]> {
        if (!Number.isInteger(merchantId) || merchantId <= 0) {
            return of([]);
        }

        return this.http
            .get<ApiResponse<SecureStorageMerchantFilesResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files`
            )
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to load Secure Storage files.");
                    }

                    return (Array.isArray(response.data?.items) ? response.data.items : []).map((item) => {
                        const createdAt =
                            (item as { createdAt?: string | null; created_at?: string | null })?.createdAt ??
                            (item as { created_at?: string | null })?.created_at ??
                            item?.uploadedAt ??
                            null;

                        const lastUpdatedAt =
                            (item as { lastUpdatedAt?: string | null; last_updated_at?: string | null })?.lastUpdatedAt ??
                            (item as { last_updated_at?: string | null })?.last_updated_at ??
                            null;

                        return {
                            ...item,
                            createdAt,
                            uploadedAt: item?.uploadedAt || createdAt || null,
                            lastUpdatedAt: lastUpdatedAt || createdAt || null,
                            aiScanStatus:
                                (item as { aiScanStatus?: string | null; ai_scan_status?: string | null })?.aiScanStatus ??
                                (item as { ai_scan_status?: string | null })?.ai_scan_status ??
                                null,
                            aiScanDetails:
                                (item as { aiScanDetails?: unknown; ai_scan_details?: unknown })?.aiScanDetails ??
                                (item as { ai_scan_details?: unknown })?.ai_scan_details ??
                                null,
                            aiScanPolicy: normalizeSecureStorageScanPolicy((item as { aiScanPolicy?: unknown }).aiScanPolicy),
                            aiScanErrorCode:
                                (item as { aiScanErrorCode?: string | null; ai_scan_error_code?: string | null })?.aiScanErrorCode ??
                                (item as { ai_scan_error_code?: string | null })?.ai_scan_error_code ??
                                null,
                            aiScanErrorMessage:
                                (item as { aiScanErrorMessage?: string | null; ai_scan_error_message?: string | null })?.aiScanErrorMessage ??
                                (item as { ai_scan_error_message?: string | null })?.ai_scan_error_message ??
                                null,
                            aiLastReadTimestamp:
                                (item as { aiLastReadTimestamp?: string | null; ai_last_read_timestamp?: string | null })
                                    ?.aiLastReadTimestamp ??
                                (item as { ai_last_read_timestamp?: string | null })?.ai_last_read_timestamp ??
                                null,
                            aiFileSummary:
                                (item as { aiFileSummary?: string | null; ai_file_summary?: string | null })?.aiFileSummary ??
                                (item as { ai_file_summary?: string | null })?.ai_file_summary ??
                                null,
                            malwareScanStatus:
                                (item as { malwareScanStatus?: string | null; malware_scan_status?: string | null })?.malwareScanStatus ??
                                (item as { malware_scan_status?: string | null })?.malware_scan_status ??
                                null,
                            malwareScanDetails:
                                (item as { malwareScanDetails?: unknown; malware_scan_details?: unknown })?.malwareScanDetails ??
                                (item as { malware_scan_details?: unknown })?.malware_scan_details ??
                                null,
                            previewSupported:
                                (item as { previewSupported?: boolean; preview_supported?: boolean })?.previewSupported ??
                                (item as { preview_supported?: boolean })?.preview_supported ??
                                false,
                            previewStatus: this.normalizePreviewStatus(
                                (item as { previewStatus?: unknown; preview_status?: unknown })?.previewStatus ??
                                    (item as { preview_status?: unknown })?.preview_status ??
                                    null
                            ),
                            tags: item?.tags && typeof item.tags === "object" ? item.tags : {},
                            aiFileTags: item?.aiFileTags && typeof item.aiFileTags === "object" ? item.aiFileTags : {},
                            aiFileAnnotations: item?.aiFileAnnotations ?? null
                        };
                    });
                })
            );
    }

    getPreviewStatusForMerchantFile(merchantId: number, fileId: number): Observable<SecureStoragePreviewStatus> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .get<ApiResponse<SecureStoragePreviewStatusResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files/${fileId}/preview`
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || SecureStorageService.PREVIEW_UNAVAILABLE_MESSAGE);
                    }

                    const normalizedStatus = this.normalizePreviewStatus(response.data);
                    if (!normalizedStatus) {
                        throw new Error(SecureStorageService.PREVIEW_UNAVAILABLE_MESSAGE);
                    }

                    return normalizedStatus;
                })
            );
    }

    requestPreviewForMerchantFile(merchantId: number, fileId: number): Observable<SecureStoragePreviewStatus> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .post<ApiResponse<SecureStoragePreviewStatusResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files/${fileId}/preview`,
                {}
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || SecureStorageService.PREVIEW_UNAVAILABLE_MESSAGE);
                    }

                    const normalizedStatus = this.normalizePreviewStatus(response.data);
                    if (!normalizedStatus) {
                        throw new Error(SecureStorageService.PREVIEW_UNAVAILABLE_MESSAGE);
                    }

                    return normalizedStatus;
                }),
                catchError((error) => {
                    const normalizedStatus = this.normalizePreviewStatus(error?.error?.data ?? null);
                    if (normalizedStatus) {
                        return of(normalizedStatus);
                    }

                    return throwError(
                        () => new Error(error?.error?.message || error?.message || SecureStorageService.PREVIEW_UNAVAILABLE_MESSAGE)
                    );
                })
            );
    }

    pollPreviewStatusForMerchantFile(
        merchantId: number,
        fileId: number,
        intervalMs = 5000
    ): Observable<SecureStoragePreviewStatus> {
        const normalizedIntervalMs = Math.max(1000, Number(intervalMs) || 5000);
        return timer(0, normalizedIntervalMs).pipe(
            switchMap(() => this.getPreviewStatusForMerchantFile(merchantId, fileId)),
            takeWhile((status) => this.isPreviewStatusPending(status), true)
        );
    }

    fetchPreviewContentBlob(status: SecureStoragePreviewStatus): Observable<Blob> {
        const normalizedUrl = this.resolveSecureStoragePreviewApiUrl(status?.contentUrl);
        if (!status?.previewAvailable || !normalizedUrl) {
            return throwError(() => new Error(SecureStorageService.PREVIEW_UNAVAILABLE_MESSAGE));
        }

        return this.http.get(normalizedUrl, { responseType: "blob" });
    }

    isPreviewStatusPending(status: SecureStoragePreviewStatus | null | undefined): boolean {
        return status?.status === "pending" || status?.status === "running";
    }

    isPreviewStatusTerminal(status: SecureStoragePreviewStatus | null | undefined): boolean {
        return !!status && !this.isPreviewStatusPending(status);
    }

    getFileAccessUrl(merchantId: number, fileId: number): Observable<SecureStorageFileAccessResult> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .get<ApiResponse<SecureStorageFileAccessResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files/${fileId}/access`
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data?.accessUrl) {
                        throw new Error(response?.message || "Unable to load the selected Secure Storage file.");
                    }

                    return {
                        id: response.data.id,
                        fileName: response.data.fileName,
                        mimeType: response.data.mimeType || null,
                        fileSizeBytes: response.data.fileSizeBytes ?? null,
                        accessUrl: this.resolveApiUrl(response.data.accessUrl)
                    };
                })
            );
    }

    uploadFilesForMerchant(
        merchantId: number,
        files: File[],
        options: {
            submissionKey: string;
            requestKey: string;
            overwriteDuplicates?: boolean;
            overwriteFileId?: number | null;
        }
    ): Observable<SecureStorageUploadResult> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Array.isArray(files) || !files.length ||
            !options?.submissionKey || !options?.requestKey) {
            return throwError(() => new Error("A merchant, file, and upload submission are required."));
        }

        const formData = new FormData();
        files.forEach((file) => {
            formData.append("files", file);
        });
        formData.append("submissionKey", options.submissionKey);
        formData.append("requestKey", options.requestKey);
        if (options?.overwriteDuplicates) {
            formData.append("overwriteDuplicates", "true");
        }
        if (Number.isInteger(options?.overwriteFileId) && Number(options?.overwriteFileId) > 0) {
            formData.append("overwriteFileId", String(options?.overwriteFileId));
        }

        return this.http
            .post<ApiResponse<SecureStorageUploadResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files`,
                formData
            )
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to upload the selected files.");
                    }

                    return {
                        persisted: !!response.data?.persisted,
                        items: Array.isArray(response.data?.items) ? response.data.items : [],
                        message: response.message || null
                    };
                })
            );
    }

    beginUploadSubmission(merchantId: number): Observable<SecureStorageUploadSubmission> {
        if (!Number.isInteger(merchantId) || merchantId <= 0) {
            return throwError(() => new Error("A valid merchant is required."));
        }

        return this.http.post<ApiResponse<SecureStorageUploadSubmission>>(
            `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/upload-submissions`, {}
        ).pipe(map((response) => {
            const receipt = response?.data;
            if (!response?.success || typeof receipt?.submissionKey !== "string" || !receipt.submissionKey) {
                throw new Error(response?.message || "Unable to begin the upload submission.");
            }
            return receipt;
        }));
    }

    completeUploadSubmission(merchantId: number, submissionKey: string,
        isCurrentSession: () => boolean = () => true): Observable<SecureStorageUploadCompletion> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !submissionKey) {
            return throwError(() => new Error("A valid upload submission is required."));
        }

        return defer(() => isCurrentSession() ? this.http.post<ApiResponse<SecureStorageUploadCompletion>>(
            `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/upload-submissions/` +
                `${encodeURIComponent(submissionKey)}/complete`,
            {}
        ) : throwError(() => new Error("The upload session changed before receipt completion."))).pipe(
            // A client-side transport failure can finish before its upload
            // request has settled on the server. Wait for that receipt only.
            retry({
                count: 10,
                delay: (error) => error?.status === 409 && error?.error?.code === "pending"
                    ? timer(3000)
                    : throwError(() => error)
            }),
            map((response) => {
                const receipt = response?.data;
                if (!response?.success || receipt?.completed !== true ||
                    !Number.isSafeInteger(receipt.successfulFileCount)) {
                    throw new Error(response?.message || "Unable to complete the upload submission.");
                }
                return receipt;
            })
        );
    }

    uploadSingleFileSubmission(
        merchantId: number,
        file: File,
        options: { overwriteDuplicates?: boolean; overwriteFileId?: number | null }
    ): Observable<SecureStorageUploadResult> {
        if (typeof globalThis.crypto?.randomUUID !== "function") {
            return throwError(() => new Error("A secure upload session could not be created."));
        }
        const requestKey = globalThis.crypto.randomUUID();
        return this.beginUploadSubmission(merchantId).pipe(
            switchMap(({ submissionKey }) => this.uploadFilesForMerchant(merchantId, [file], {
                ...options,
                submissionKey,
                requestKey
            }).pipe(
                // Complete the submission after either an upload result or an
                // upload error, then preserve that original outcome for callers.
                materialize(),
                take(1),
                concatMap((outcome) => this.completeUploadSubmission(merchantId, submissionKey).pipe(
                    map(() => outcome)
                )),
                dematerialize()
            )),
            // Once started, finish the receipt even if its modal is closed or
            // its component is destroyed during the upload.
            shareReplay({ bufferSize: 1, refCount: false })
        );
    }

    deleteFileForMerchant(merchantId: number, fileId: number): Observable<void> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .delete<ApiResponse<SecureStorageDeleteFileResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files/${fileId}`
            )
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to delete the selected Secure Storage file.");
                    }
                })
            );
    }

    addTagForMerchantFile(merchantId: number, fileId: number, key: string, value: string): Observable<Record<string, string>> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .patch<ApiResponse<SecureStorageUpdateTagsResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files/${fileId}/tags`,
                { operation: "add", key, value }
            )
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to update file tags.");
                    }

                    return response.data?.tags && typeof response.data.tags === "object" ? response.data.tags : {};
                })
            );
    }

    removeTagForMerchantFile(merchantId: number, fileId: number, key: string): Observable<Record<string, string>> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .patch<ApiResponse<SecureStorageUpdateTagsResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files/${fileId}/tags`,
                { operation: "remove", key }
            )
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to update file tags.");
                    }

                    return response.data?.tags && typeof response.data.tags === "object" ? response.data.tags : {};
                })
            );
    }

    updateAnnotationsForMerchantFile(merchantId: number, fileId: number, annotations: unknown): Observable<{
        id: number;
        annotations: unknown;
        aiScanStatus: string | null;
        aiScanPolicy: SecureStorageScanPolicy | null;
        aiScanDetails: unknown;
        aiScanErrorCode: string | null;
        aiScanErrorMessage: string | null;
        lastUpdatedAt: string | null;
    }> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .patch<ApiResponse<SecureStorageUpdateAnnotationsResponse>>(
                `${this.apiBaseUrl}/secure-storage/merchants/${merchantId}/files/${fileId}/annotations`,
                { annotations }
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Unable to update file annotations.");
                    }

                    return {
                        id: Number(response.data.id),
                        annotations: response.data.annotations ?? null,
                        aiScanStatus: response.data.aiScanStatus || null,
                        aiScanDetails: response.data.aiScanDetails ?? null,
                        aiScanPolicy: normalizeSecureStorageScanPolicy(response.data.aiScanPolicy),
                        aiScanErrorCode: response.data.aiScanErrorCode || null,
                        aiScanErrorMessage: response.data.aiScanErrorMessage || null,
                        lastUpdatedAt: response.data.lastUpdatedAt || null
                    };
                })
            );
    }

    enqueueAiScanForMerchantFile(merchantId: number, fileId: number, options: SecureStorageScanEnqueueOptions = {}): Observable<SecureStorageEnqueueAiScanResult> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .post<ApiResponse<unknown>>(
                `${this.apiBaseUrl}/ai/secure-storage/files/${fileId}/enqueue`,
                { merchantId, overrideAcknowledged: options.overrideAcknowledged,
                    expectedScan: options.expectedScan ? { jobId: options.expectedScan.jobId,
                        sourceHash: options.expectedScan.sourceHash, sourceVersion: options.expectedScan.sourceVersion } : undefined }
            )
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to queue a retry AI scan for the selected file.");
                    }

                    return { queued: true };
                })
            );
    }

    cancelAiScanForMerchantFile(merchantId: number, fileId: number): Observable<SecureStorageCancelAiScanResult> {
        if (!Number.isInteger(merchantId) || merchantId <= 0 || !Number.isInteger(fileId) || fileId <= 0) {
            return throwError(() => new Error("A valid merchant and file selection is required."));
        }

        return this.http
            .post<ApiResponse<{ status?: string | null }>>(
                `${this.apiBaseUrl}/ai/secure-storage/files/${fileId}/cancel`,
                { merchantId }
            )
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to cancel the AI scan for the selected file.");
                    }

                    return {
                        canceled: true,
                        status: response.data?.status || "canceled"
                    };
                })
            );
    }

    downloadFileBlob(accessUrl: string): Observable<Blob> {
        const normalizedUrl = this.resolveApiUrl(accessUrl);
        if (!normalizedUrl) {
            return throwError(() => new Error("A valid file download URL is required."));
        }

        return this.http.get(normalizedUrl, { responseType: "blob" });
    }

    isOrganizationDisabledErrorMessage(message: string | null | undefined): boolean {
        const normalizedMessage = typeof message === "string" ? message.trim().toLowerCase() : "";
        if (!normalizedMessage) {
            return false;
        }

        return (
            normalizedMessage.includes("disabled for your organization") ||
            normalizedMessage.includes("secure storage is disabled") ||
            normalizedMessage.includes("organization secure storage")
        );
    }

    private resolveMerchantContextErrorMessage(error: {
        status?: number | null;
        message?: string | null;
    }): string {
        const normalizedMessage = typeof error.message === "string" ? error.message.trim() : "";
        const normalizedMessageLower = normalizedMessage.toLowerCase();
        const looksLikeOrganizationDisabled =
            normalizedMessageLower.includes("disabled for your organization") ||
            normalizedMessageLower.includes("secure storage is disabled") ||
            normalizedMessageLower.includes("organization secure storage");
        const looksLikeAccessDenial =
            normalizedMessageLower.includes("do not have access") ||
            normalizedMessageLower.includes("cannot access") ||
            normalizedMessageLower.includes("access denied");

        if (looksLikeOrganizationDisabled) {
            return SecureStorageService.DISABLED_ORGANIZATION_MESSAGE;
        }

        if (error.status === 403 || error.status === 404 || looksLikeAccessDenial) {
            return SecureStorageService.ACCESS_DENIED_MERCHANT_CONTEXT_MESSAGE;
        }

        return normalizedMessage || SecureStorageService.LOAD_MERCHANT_CONTEXT_FAILURE_MESSAGE;
    }

    private normalizePreviewStatus(value: unknown): SecureStoragePreviewStatus | null {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
            return null;
        }

        const record = value as Record<string, unknown>;
        const normalizedStatus = this.normalizePreviewStatusValue(record["status"]);
        return {
            fileId: this.normalizeNumber(record["fileId"] ?? record["file_id"]),
            fileName: this.normalizeNullableString(record["fileName"] ?? record["file_name"]),
            mimeType: this.normalizeNullableString(record["mimeType"] ?? record["mime_type"]),
            fileSizeBytes: this.normalizeNumber(record["fileSizeBytes"] ?? record["file_size_bytes"]),
            previewSupported: record["previewSupported"] === true || record["preview_supported"] === true,
            previewable: record["previewable"] === true,
            status: normalizedStatus,
            message:
                this.normalizeNullableString(record["message"]) ||
                this.normalizeNullableString(record["errorMessage"] ?? record["error_message"]) ||
                SecureStorageService.PREVIEW_UNAVAILABLE_MESSAGE,
            previewAvailable: record["previewAvailable"] === true || record["preview_available"] === true,
            contentUrl: this.normalizeNullableString(record["contentUrl"] ?? record["content_url"]),
            rendererVersion: this.normalizeNullableString(record["rendererVersion"] ?? record["renderer_version"]),
            requestedAt: this.normalizeNullableString(record["requestedAt"] ?? record["requested_at"]),
            lastRequestedAt: this.normalizeNullableString(record["lastRequestedAt"] ?? record["last_requested_at"]),
            startedAt: this.normalizeNullableString(record["startedAt"] ?? record["started_at"]),
            completedAt: this.normalizeNullableString(record["completedAt"] ?? record["completed_at"]),
            nextAttemptAt: this.normalizeNullableString(record["nextAttemptAt"] ?? record["next_attempt_at"]),
            previewByteCount: this.normalizeNumber(record["previewByteCount"] ?? record["preview_byte_count"]),
            previewPageCount: this.normalizeNumber(record["previewPageCount"] ?? record["preview_page_count"]),
            previewTruncated: record["previewTruncated"] === true || record["preview_truncated"] === true,
            previewMetadata: this.normalizePreviewMetadata(record["previewMetadata"] ?? record["preview_metadata"]),
            errorCode: this.normalizeNullableString(record["errorCode"] ?? record["error_code"]),
            errorMessage: this.normalizeNullableString(record["errorMessage"] ?? record["error_message"])
        };
    }

    private normalizePreviewStatusValue(value: unknown): SecureStoragePreviewStatusValue {
        const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
        if (
            normalized === "not_requested" ||
            normalized === "pending" ||
            normalized === "running" ||
            normalized === "succeeded" ||
            normalized === "failed" ||
            normalized === "unavailable" ||
            normalized === "unsupported" ||
            normalized === "missing_source_hash" ||
            normalized === "malware_not_clean"
        ) {
            return normalized;
        }

        return "unavailable";
    }

    private normalizeNumber(value: unknown): number | null {
        if (value === null || value === undefined || value === "") {
            return null;
        }

        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
    }

    private normalizeNullableString(value: unknown): string | null {
        const normalized = typeof value === "string" ? value.trim() : "";
        return normalized || null;
    }

    private normalizePreviewMetadata(value: unknown): Record<string, unknown> | null {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
            return null;
        }

        const normalizedEntries = Object.entries(value as Record<string, unknown>)
            .slice(0, 12)
            .reduce<Record<string, unknown>>((metadata, [key, metadataValue]) => {
                const normalizedKey = this.normalizeNullableString(key)?.slice(0, 64);
                if (!normalizedKey) {
                    return metadata;
                }

                if (
                    metadataValue === null ||
                    typeof metadataValue === "boolean"
                ) {
                    metadata[normalizedKey] = metadataValue;
                    return metadata;
                }

                if (typeof metadataValue === "number" && Number.isFinite(metadataValue)) {
                    metadata[normalizedKey] = metadataValue;
                    return metadata;
                }

                if (typeof metadataValue === "string") {
                    metadata[normalizedKey] = metadataValue.slice(0, 500);
                }

                return metadata;
            }, {});

        return Object.keys(normalizedEntries).length ? normalizedEntries : null;
    }

    private resolveSecureStoragePreviewApiUrl(url: string | null | undefined): string {
        const trimmed = typeof url === "string" ? url.trim() : "";
        if (!trimmed || !trimmed.startsWith("/") || trimmed.startsWith("//")) {
            return "";
        }

        if (!trimmed.startsWith("/api/secure-storage/")) {
            return "";
        }

        return this.resolveApiUrl(trimmed);
    }

    private resolveApiUrl(url: string | null | undefined): string {
        const trimmed = typeof url === "string" ? url.trim() : "";
        if (!trimmed) {
            return "";
        }

        if (/^https?:\/\//i.test(trimmed)) {
            return trimmed;
        }

        if (!trimmed.startsWith("/")) {
            return trimmed;
        }

        try {
            const origin = new URL(this.apiBaseUrl).origin;
            return `${origin}${trimmed}`;
        } catch (error) {
            return trimmed;
        }
    }
}
